import type {
  Communication,
  Observation,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { z } from 'zod'
import { CommentCommunication } from '#src/models/communications/CommentCommunication.ts'
import { createReference } from '#src/references.ts'

// https://docs.elationhealth.com/reference/the-non-visit-note-object
export const nonVisitNoteSchema = z.object({
  id: z.number(),
  type: z.enum(['email', 'nonvisit', 'phone', 'DIRECT']),
  notes: z.array(
    z.object({
      id: z.number(),
      author: z.number(),
      text: z.string(),
    })
  ),
  bullets: z.array(
    z.object({
      id: z.number(),
      author: z.number(),
      text: z.string(),
    })
  ),
  tags: z.array(
    z.object({
      code: z.string().nullable().optional(),
      code_type: z.number().nullable().optional(),
      concept_name: z.string().nullable().optional(),
      created_date: z.string(), // ISO8601 datetime string
      deleted_date: z.string().nullable(), // ISO8601 datetime string
      description: z.string(),
      id: z.number(),
      value: z.string(),
    })
  ),
  patient: z.number(),
  practice: z.number().nullable().optional(),
  document_date: z.string().nullable(), // ISO8601 datetime string
  chart_date: z.string().nullable(), // ISO8601 datetime string
  signed_date: z.string().nullable(), // ISO8601 datetime string
  signed_by: z.number().nullable(),
  created_date: z.string(), // ISO8601 datetime string
  deleted_date: z.string().nullable(), // ISO8601 datetime string
})

export type NonVisitNote = z.infer<typeof nonVisitNoteSchema>

export function toFhir({
  nonVisitNote,
  patient,
  author,
  observations,
}: {
  nonVisitNote: NonVisitNote
  patient: Patient
  author: Practitioner
  observations?: Reference<Observation>[]
}): CommentCommunication {
  const notes = compact(
    nonVisitNote.notes.map((note) => note.text.trim())
  ).join('\n')
  const tags = compact(nonVisitNote.tags.map((tag) => tag.value.trim())).join(
    '\n'
  )

  const message = [notes, tags].join('\n')

  const communication = new CommentCommunication({
    partOf: [createReference(patient)],
    message,
    elationCategory: nonVisitNote.type,
    sent:
      nonVisitNote.document_date ??
      nonVisitNote.chart_date ??
      nonVisitNote.created_date,
    sender: createReference(author),
  })
  communication.elationId = nonVisitNote.id.toString()
  communication.externalResourceJSON = JSON.stringify(nonVisitNote)

  // Add observations as payload if they exist
  if (observations && observations.length > 0) {
    const fhirResource = communication.toFhirResource()
    const existingPayload = fhirResource.payload ?? []
    const observationPayloads = observations.map((ref) => ({
      contentReference: ref,
    }))
    fhirResource.payload = [...existingPayload, ...observationPayloads]
    // Return a new CommentCommunication with the updated payload
    return CommentCommunication.fromFhirResource(fhirResource)
  }

  return communication
}

export function fromFhir(_communication: Communication): NonVisitNote {
  // We don't currently have a use case for converting a FHIR Communication to an Elation NonVisitNote
  // as we only sync uni-directionally from Elation
  throw new Error('Not implemented')
}
